import { portalText } from './i18n';
import { isWholeCountryLocation, suggestedRadiusForLocation } from './searchRadius';
import { searchServiceHost } from './searchHost';

const instances = new WeakMap();

export function initSearchBoxes(root = document) {
    root.querySelectorAll('.location-search, .category-search').forEach(select => {
        if (!instances.has(select)) {
            instances.set(select, new SearchCombobox(select));
        }
    });
}

export function getFirstSearchInput() {
    return document.querySelector('.select2-search__field');
}

class SearchCombobox {
    constructor(select) {
        this.select = select;
        this.isLocation = select.classList.contains('location-search');
        this.selected = this.readSelectedOption();
        this.results = [];
        this.highlightedIndex = -1;
        this.abortController = null;
        this.build();
        this.bind();
        this.renderSelection();
        // The server-rendered value is authoritative while restoring a search
        // (including page changes). Suggest a radius only after a user chooses
        // a different location.
        this.updateRadius(false);
    }

    build() {
        this.select.classList.add('select2-hidden-accessible');
        this.select.tabIndex = -1;
        this.select.setAttribute('aria-hidden', 'true');

        this.container = document.createElement('span');
        this.container.className = 'select2 select2-container select2-container--default';
        this.container.style.width = '100%';

        this.selection = document.createElement('span');
        this.selection.className = 'selection';
        this.selection.innerHTML = '<span class="select2-selection select2-selection--multiple" role="combobox" aria-haspopup="listbox" aria-expanded="false"><ul class="select2-selection__rendered"></ul><span class="select2-search select2-search--inline"><textarea class="select2-search__field" rows="1" autocomplete="off" autocapitalize="none" spellcheck="false" role="searchbox" aria-autocomplete="list"></textarea></span></span>';

        this.input = this.selection.querySelector('.select2-search__field');
        this.input.placeholder = this.select.dataset.placeholder || this.select.getAttribute('placeholder') || '';
        this.combobox = this.selection.querySelector('[role="combobox"]');

        this.dropdown = document.createElement('span');
        this.dropdown.className = 'select2-container select2-container--default select2-container--open portal-search-dropdown';
        this.dropdown.hidden = true;
        this.dropdown.innerHTML = '<span class="select2-dropdown select2-dropdown--below"><span class="select2-results"><ul class="select2-results__options" role="listbox"></ul></span></span>';
        this.list = this.dropdown.querySelector('[role="listbox"]');
        this.list.id = `portal-search-results-${SearchCombobox.nextId++}`;
        this.input.setAttribute('aria-controls', this.list.id);

        this.container.append(this.selection);
        this.select.after(this.container, this.dropdown);
    }

    bind() {
        this.combobox.addEventListener('pointerdown', event => {
            event.preventDefault();
            this.open();
            this.input.focus();
        });
        this.combobox.addEventListener('click', event => {
            event.preventDefault();
            this.open();
            this.input.focus();
        });
        this.input.addEventListener('focus', () => this.open());
        this.input.addEventListener('input', () => this.load(this.input.value));
        this.input.addEventListener('keydown', event => this.handleKeydown(event));
        this.list.addEventListener('mousedown', event => event.preventDefault());
        this.list.addEventListener('click', event => {
            const option = event.target.closest('[data-result-index]');
            if (option) {
                this.choose(this.results[Number(option.dataset.resultIndex)]);
            }
        });
        document.addEventListener('pointerdown', event => {
            if (!this.container.contains(event.target) && !this.dropdown.contains(event.target)) {
                this.close();
            }
        });
    }

    readSelectedOption() {
        const option = this.select.selectedOptions[0];
        return option && option.value !== '' ? {id: option.value, text: option.textContent.trim()} : null;
    }

    open() {
        if (!this.dropdown.hidden) {
            return;
        }

        this.dropdown.hidden = false;
        this.container.classList.add('select2-container--open');
        this.combobox.setAttribute('aria-expanded', 'true');
        this.input.value = '';
        this.renderSelection();

        if (window.matchMedia('(max-width: 549px)').matches) {
            this.select.closest('.container')?.scrollIntoView({block: 'start'});
        }

        this.load('');
    }

    close() {
        if (this.dropdown.hidden) {
            return;
        }

        this.abortController?.abort();
        this.dropdown.hidden = true;
        this.container.classList.remove('select2-container--open');
        this.combobox.setAttribute('aria-expanded', 'false');
        this.highlightedIndex = -1;
        this.input.value = '';
        this.renderSelection();
    }

    async load(term) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.renderMessage(portalText('loading'));

        const host = searchServiceHost(window.location.host);
        const path = this.isLocation ? '/lokace' : '/kategorie';
        const url = new URL(`${window.location.protocol}//${host}${path}`);
        url.searchParams.set('q', term);

        try {
            const response = await fetch(url, {
                headers: {'Accept': 'application/json'},
                signal: this.abortController.signal,
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const payload = await response.json();
            this.results = Array.isArray(payload.results) ? payload.results : [];
            const customTerm = term.trim();
            if (!this.isLocation && customTerm !== '' && !this.results.some(result => result.text === customTerm)) {
                this.results.unshift({id: customTerm, text: customTerm});
            }
            this.highlightedIndex = this.results.length > 0 ? 0 : -1;
            this.renderResults();
        } catch (error) {
            if (error.name !== 'AbortError') {
                this.results = [];
                this.highlightedIndex = -1;
                this.renderMessage(portalText('loadError'));
            }
        }
    }

    renderSelection() {
        const rendered = this.selection.querySelector('.select2-selection__rendered');
        rendered.querySelector('.select2-selection__choice')?.remove();

        if (this.selected && this.dropdown.hidden) {
            this.input.hidden = true;
            const choice = document.createElement('li');
            choice.className = 'select2-selection__choice';
            choice.title = this.selected.text;
            choice.innerHTML = '<span class="select2-selection__choice__display"></span>';
            choice.querySelector('span').textContent = this.selected.text;
            rendered.prepend(choice);
            this.input.placeholder = '';
        } else {
            this.input.hidden = false;
            this.input.placeholder = this.select.dataset.placeholder || this.select.getAttribute('placeholder') || '';
        }
    }

    renderResults() {
        this.list.replaceChildren();
        if (this.results.length === 0) {
            this.renderMessage(portalText('empty'));
            return;
        }

        this.results.forEach((result, index) => {
            const option = document.createElement('li');
            option.className = 'select2-results__option';
            option.dataset.resultIndex = String(index);
            option.id = `${this.list.id}-${index}`;
            option.setAttribute('role', 'option');
            option.textContent = result.text;
            if (index === this.highlightedIndex) {
                option.classList.add('select2-results__option--highlighted');
                option.setAttribute('aria-selected', 'true');
                this.input.setAttribute('aria-activedescendant', option.id);
            }
            this.list.append(option);
        });
    }

    renderMessage(message) {
        this.list.replaceChildren();
        const option = document.createElement('li');
        option.className = 'select2-results__option portal-search-message';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-disabled', 'true');
        option.textContent = message;
        this.list.append(option);
    }

    handleKeydown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            this.open();
            if (this.results.length > 0) {
                const direction = event.key === 'ArrowDown' ? 1 : -1;
                this.highlightedIndex = (this.highlightedIndex + direction + this.results.length) % this.results.length;
                this.renderResults();
                this.list.querySelector('.select2-results__option--highlighted')?.scrollIntoView({block: 'nearest'});
            }
            return;
        }

        if (event.key === 'Enter' && !this.dropdown.hidden && this.highlightedIndex >= 0) {
            event.preventDefault();
            this.choose(this.results[this.highlightedIndex]);
        }
    }

    choose(result) {
        if (!result) {
            return;
        }

        this.selected = {id: String(result.id), text: String(result.text)};
        this.select.replaceChildren(new Option(this.selected.text, this.selected.id, true, true));
        this.select.dispatchEvent(new Event('change', {bubbles: true}));
        this.updateRadius();
        this.close();
        document.querySelector('button.search')?.focus();
    }

    updateRadius(applySuggestion = true) {
        if (!this.isLocation) {
            return;
        }

        const range = document.querySelector('.range');
        if (!range) {
            return;
        }

        if (!this.selected) {
            range.style.display = 'none';
            range.value = '0';
            return;
        }

        const location = this.selected.id;

        range.style.display = isWholeCountryLocation(location) ? 'none' : 'block';
        if (applySuggestion) {
            range.value = String(suggestedRadiusForLocation(location));
        }
    }
}

SearchCombobox.nextId = 1;
